/*
 * Current-User Account List
 *
 * Features:
 * - Displays the authenticated user's accounts with search, export, and responsive cards
 * - Reuses and invalidates the canonical user-account Query cache
 *
 * Changes:
 * - 2026-08-26: Added distinct collection/search empty states with non-mutating recovery actions
 * - 2026-08-06: Replaced local fetch/removal state with TanStack Query
 */

import { FC, useEffect, useMemo } from 'react';
import { Account } from '../../models/Account';
import { THEME_CLASSES } from '../../constants/themeConstants';
import AccountCard from './AccountCard';
import { useTeams } from '../../context/TeamContext';
import { useChatContext } from '../../context/ChatContext';
import Spinner from '../common/Spinner';
import { filterAccounts, getAccountDisplayFields } from '../../utils/accountUtils';
import { getResponsiveGridClassesWithTransitions } from '../../utils/responsiveUtils';
import { useRemoveUserAccountMutation, useUserAccountsQuery } from '../../query/accountQueries';
import { Button } from '../primitives';

interface AccountListProps {
  searchValue: string;
  onRowCountChange?: (count: number) => void;
  onDataChange?: (data: Account[], columns: Array<{field: string, headerName: string}>) => void;
  onBrowseAllAccounts: () => void;
  onClearSearch: () => void;
}

const AccountList: FC<AccountListProps> = ({
  searchValue,
  onRowCountChange,
  onDataChange,
  onBrowseAllAccounts,
  onClearSearch,
}) => {
  const { currentUserId } = useTeams();
  const { isChatVisible } = useChatContext();
  const accountsQuery = useUserAccountsQuery(currentUserId);
  const removeAccount = useRemoveUserAccountMutation();
  const accounts = accountsQuery.data ?? [];
  const loading = Boolean(currentUserId && accountsQuery.isPending);
  const error = accountsQuery.error instanceof Error
    ? accountsQuery.error.message
    : accountsQuery.error ? 'Failed to load accounts' : null;

  const keywords = useMemo(() => searchValue.toLowerCase().split(/\s+/).filter(Boolean), [searchValue]);

  const filteredAccounts = useMemo(() => {
    // Specify the fields we want to search in
    const searchFields = getAccountDisplayFields().map((field: any) => field.key);
    return filterAccounts(accounts, searchValue, searchFields);
  }, [accounts, searchValue]);

  // Update parent with row count
  useEffect(() => {
    onRowCountChange?.(filteredAccounts.length);
  }, [filteredAccounts.length, onRowCountChange]);

  // Update parent with filtered data and columns for export
  useEffect(() => {
    if (onDataChange && filteredAccounts.length > 0) {
      // Convert display fields to export column format
      const exportColumns = getAccountDisplayFields().map(field => ({
        field: String(field.key),
        headerName: field.label
      }));

      onDataChange(filteredAccounts, exportColumns);
    }
  }, [filteredAccounts, onDataChange]);

  // Custom delete handler that uses DELETE_USER_ACCOUNT
  const handleDeleteAccount = async (id: number) => {
    if (!currentUserId) return;

    try {
      await removeAccount.mutateAsync({
        userId: currentUserId,
        id: id
      });
    } catch (err) {
      console.error('Error removing account from user list:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center h-64 flex items-center justify-center">
        {error}
      </div>
    );
  }

  // Calculate responsive grid classes based on chat visibility
  const gridClasses = getResponsiveGridClassesWithTransitions('accounts', isChatVisible);

  return (
    <>
      {filteredAccounts.length === 0 ? (
        <div className={`flex h-64 flex-col items-center justify-center gap-3 px-6 text-center ${THEME_CLASSES.text.secondary}`}>
          <h2 className={`text-base font-semibold ${THEME_CLASSES.text.primary}`}>
            {searchValue ? 'No accounts match your search' : 'No saved accounts yet'}
          </h2>
          <p className="max-w-md text-sm">
            {searchValue
              ? 'Try another term or clear the current search.'
              : 'Browse all accounts to add the records you work with most often.'}
          </p>
          <Button variant="secondary" size="sm" onClick={searchValue ? onClearSearch : onBrowseAllAccounts}>
            {searchValue ? 'Clear search' : 'Browse all accounts'}
          </Button>
        </div>
      ) : (
        <div className={`w-full h-fit ${gridClasses} p-6 ${THEME_CLASSES.background}`}>
          {filteredAccounts.map((account) => (
            <AccountCard
              key={account.id}
              account={account}
              keywords={keywords}
              onDelete={handleDeleteAccount}
              showDeleteButton={true}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default AccountList;
